import type { ApiErrorShape } from '../types';
import { cacheValue, enqueueMutation, listMutations, readCachedValue, removeMutation, saveConflict, type OfflineConflict } from './offline';
import { clearNativeTokens, getNativeAccessToken, getNativeRefreshToken, isNativeApp, saveNativeTokens } from './nativeAuth';

const NATIVE_API_ORIGIN = 'https://hengri-wellness.pages.dev';

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message);
  }
}

let csrfToken: string | null = null;

function apiUrl(path: string): string {
  return isNativeApp() && path.startsWith('/') ? `${NATIVE_API_ORIGIN}${path}` : path;
}

async function authenticatedFetch(path: string, init: RequestInit, allowRefresh = true): Promise<Response> {
  const headers = new Headers(init.headers);
  const accessToken = getNativeAccessToken();
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  let response = await fetch(apiUrl(path), { ...init, headers });
  const refreshToken = getNativeRefreshToken();
  if (response.status !== 401 || !isNativeApp() || !allowRefresh || !refreshToken || path.endsWith('/auth/refresh')) return response;
  const refreshResponse = await fetch(apiUrl('/api/v1/auth/refresh'), {
    method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ refreshToken })
  });
  if (!refreshResponse.ok) {
    await clearNativeTokens();
    return response;
  }
  const fresh = await refreshResponse.json() as { accessToken: string; refreshToken: string; expiresIn: number };
  await saveNativeTokens(fresh.accessToken, fresh.refreshToken, fresh.expiresIn);
  headers.set('Authorization', `Bearer ${fresh.accessToken}`);
  response = await fetch(apiUrl(path), { ...init, headers });
  return response;
}

export function setCsrfToken(value: string | null): void {
  csrfToken = value;
}

async function parseResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type') ?? '';
  const body = contentType.includes('application/json') ? await response.json() as T | ApiErrorShape : null;
  if (!response.ok) {
    const error = body as ApiErrorShape | null;
    throw new ApiError(response.status, error?.error.code ?? 'REQUEST_FAILED', error?.error.message ?? '請求失敗', error?.error.details);
  }
  return body as T;
}

export async function apiGet<T>(path: string, options: { cacheKey?: string; signal?: AbortSignal } = {}): Promise<T> {
  try {
    const response = await authenticatedFetch(path, { credentials: 'include', signal: options.signal, headers: { Accept: 'application/json' } });
    const data = await parseResponse<T>(response);
    if (options.cacheKey) await cacheValue(options.cacheKey, data);
    return data;
  } catch (error) {
    if (options.cacheKey) {
      const cached = await readCachedValue<T>(options.cacheKey);
      if (cached) return cached;
    }
    throw error;
  }
}

export async function apiDownload(path: string): Promise<void> {
  const response = await authenticatedFetch(path, { credentials: 'include', headers: { Accept: '*/*' } });
  if (!response.ok) await parseResponse(response);
  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') ?? '';
  const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] ?? 'hengri-export';
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(href), 1000);
}

export async function apiMutation<T>(
  path: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  body?: unknown,
  options: { queueOffline?: boolean; version?: number; idempotencyKey?: string } = {}
): Promise<T | { queued: true; id: string }> {
  const id = options.idempotencyKey ?? crypto.randomUUID();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'Idempotency-Key': id
  };
  if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
  if (options.version !== undefined) headers['If-Match'] = String(options.version);

  try {
    const response = await authenticatedFetch(path, {
      method,
      credentials: 'include',
      headers,
      body: body === undefined ? undefined : JSON.stringify(body)
    });
    return await parseResponse<T>(response);
  } catch (error) {
    const offline = !navigator.onLine || error instanceof TypeError;
    if (!options.queueOffline || !offline) throw error;
    await enqueueMutation({ id, path, method, body, headers, createdAt: new Date().toISOString() });
    return { queued: true, id };
  }
}

export async function flushOfflineQueue(): Promise<{ sent: number; conflicts: OfflineConflict[] }> {
  if (!navigator.onLine) return { sent: 0, conflicts: [] };
  const items = await listMutations();
  let sent = 0;
  const conflicts: OfflineConflict[] = [];
  for (const item of items.sort((a, b) => a.createdAt.localeCompare(b.createdAt))) {
    try {
      const response = await authenticatedFetch(item.path, {
        method: item.method,
        credentials: 'include',
        headers: { ...item.headers, ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}) },
        body: item.body === undefined ? undefined : JSON.stringify(item.body)
      });
      if (response.status === 409) {
        const payload = await response.json().catch(() => null) as ApiErrorShape | null;
        const conflict: OfflineConflict = {
          id: item.id,
          mutation: item,
          code: payload?.error.code ?? 'VERSION_CONFLICT',
          message: payload?.error.message ?? '資料已在其他裝置更新',
          details: payload?.error.details,
          detectedAt: new Date().toISOString()
        };
        await saveConflict(conflict);
        await removeMutation(item.id);
        conflicts.push(conflict);
        continue;
      }
      if (response.ok) {
        await removeMutation(item.id);
        sent += 1;
      }
    } catch {
      break;
    }
  }
  return { sent, conflicts };
}
